import { Clock, RefreshCw } from 'lucide-react'
import { useEffect, useState } from 'react'

import { Button, useReloadBlocked } from '@krp/ui-kit'

import './updateBanner.css'
import { reloadForUpdate } from './reload'
import { useServerBuild } from './serverBuild'
import { BUILD_ID, isNewVersion } from './version'

/** На сколько «Позже» прячет плашку. */
export const SNOOZE_MS = 30 * 60 * 1000

/** Запас сверх срока «Позже»: плашка возвращается строго после его конца, а не на границе. */
const SNOOZE_END_MARGIN_MS = 50

export interface UpdateBannerProps {
  /** Идентификатор своей сборки; нет — проверка выключена (dev, тесты). */
  currentId?: string | undefined
  snoozeMs?: number
  /** Браузерный fetch от composition root: нужен для обхода кэша при обновлении. */
  fetchImpl?: typeof fetch
  reload?: () => Promise<void>
}

/**
 * Плашка «Вышла новая версия» (FB43): появляется, когда ответ сервера назвал другую сборку, чем наша.
 * Состояние различается не только цветом: значок и текст. Закрыть насовсем
 * нельзя — только «Позже» на {@link SNOOZE_MS}: клиент, застрявший на старой сборке, опаснее лишней плашки.
 * Пока открыт чек или идёт оплата (`useReloadBlock`), страницу не перезагружаем: кнопка выключена, а плашка
 * говорит «Обновление после закрытия чека». Сама страница не перезагружается никогда — только по кнопке.
 */
export function UpdateBanner({ currentId = BUILD_ID, snoozeMs = SNOOZE_MS, fetchImpl, reload }: UpdateBannerProps) {
  // Сервер называет сборку в заголовке каждого ответа; опроса по таймеру нет.
  const found = isNewVersion(currentId, useServerBuild())
  const doReload = reload ?? (() => (fetchImpl === undefined ? Promise.resolve() : reloadForUpdate(fetchImpl)))
  const blocked = useReloadBlocked()
  const [snoozedUntil, setSnoozedUntil] = useState(0)
  const [now, setNow] = useState(() => Date.now())
  const [busy, setBusy] = useState(false)

  // Откладывание кончается само: плашка возвращается по таймеру, а не при следующей проверке.
  useEffect(() => {
    if (snoozedUntil === 0) return undefined
    const timer = window.setTimeout(() => setNow(Date.now()), Math.max(0, snoozedUntil - Date.now()) + SNOOZE_END_MARGIN_MS)
    return () => window.clearTimeout(timer)
  }, [snoozedUntil])

  if (!found || now < snoozedUntil) return null

  return (
    <div className="update-banner" role="status" aria-live="polite" data-blocked={blocked || undefined}>
      <RefreshCw size={16} aria-hidden="true" />
      <span className="update-banner__text">
        Вышла новая версия.
        {blocked && <strong> Обновление после закрытия чека.</strong>}
      </span>
      <Button
        variant="primary"
        data-size="sm"
        busy={busy}
        disabled={blocked}
        disabledReason={blocked ? 'Открыт чек или идёт оплата: обновление после закрытия чека' : null}
        icon={<RefreshCw size={14} aria-hidden="true" />}
        onClick={() => {
          setBusy(true)
          void doReload().finally(() => setBusy(false))
        }}
        actionId="shell.update"
      >
        Обновить
      </Button>
      <Button
        variant="ghost"
        data-size="sm"
        icon={<Clock size={14} aria-hidden="true" />}
        onClick={() => {
          const at = Date.now()
          setNow(at)
          setSnoozedUntil(at + snoozeMs)
        }}
        actionId="shell.update-later"
      >
        Позже
      </Button>
    </div>
  )
}
